import { Auth, signInWithCustomToken } from '@firebase/auth';
import { AccountInfo } from '@azure/msal-browser';
import { asyncThunkCreator, buildCreateSlice, } from '@reduxjs/toolkit';
import { RootState } from '../store.ts';

export interface User {
    readonly email: string,
    readonly displayName: string | null;
    readonly photoURL: string | null;
    readonly roles: Array<string> | null;
}

export interface UserState {
    user: User | null
    status: LoginStatus
}

export enum LoginStatus {
    Anonymous = 'anonymous',
    Authenticated = 'authenticated'
}

const initialState: UserState = {
    user: null,
    status: LoginStatus.Anonymous,
}

export const createAppSlice = buildCreateSlice({
    creators: { asyncThunk: asyncThunkCreator },
})

// Response shape of POST /api/session/firebase-token (contracts/entra-token-claims.md,
// research.md Decision 16). The Microsoft access token is sent as the request's own
// `Authorization: Bearer` credential; there is no request body.
interface FirebaseTokenExchangeResponse {
    customToken: string;
}

export const userSlice = createAppSlice({
    name: 'user',
    initialState,
    reducers: (create) => {
        // Bridges the Microsoft-authenticated (MSAL) session to a Firebase identity via the
        // backend token-exchange endpoint, rather than the Firebase-native
        // `signInWithCredential('microsoft.com')` flow (dead per research.md Decision 4 — Firebase
        // does not support it for Microsoft). The Microsoft *access* token (not the ID token) is
        // the request's own bearer credential; the backend validates it (quarkus-oidc) and mints a
        // Firebase custom token carrying only `groups` — there is no `photoURL` custom claim on
        // this path, so it isn't read off `idTokenResult.claims`.
        //
        // `email`/`displayName` are read off the **MSAL account** (`payload.account`), not the
        // Firebase `User` record. They used to come from `userCredential.user` on the assumption
        // that `FirebaseCustomTokenService`'s pre-mint upsert (email/displayName/photoURL onto the
        // Firebase user record) had already propagated by the time `signInWithCustomToken`
        // resolves — sometimes true, sometimes not, and when not, `user.email` came back `null`
        // and broke signup (it sends `email` as the Firestore `userId`). `payload.account.username`
        // is populated synchronously by MSAL itself, with no dependency on any server-side write
        // having landed — and it's the exact same claim (`preferred_username`/`upn`) the backend's
        // `SessionResource.exchangeFirebaseToken()` already uses as `userId`, so the two stay
        // consistent by construction. `photoURL` is built from that same value against
        // `UserResource`'s `GET /user/{userId}/photo`.
        const loginThunk = create.asyncThunk(async (payload: { accessToken: string, auth: Auth, account: AccountInfo }, thunkAPI): Promise<User> => {
            const state = thunkAPI.getState() as RootState
            const config = state.config.config;

            if (config === undefined) {
                console.log("Invalid state; config was undefined")
                throw new Error("No config found")
            }

            // Leading slash matters: it's what makes this resolve root-relative to the origin in
            // deployed environments (config.apiEndpoint === "", Firebase Hosting rewrites
            // /api/** to the backend) regardless of the current page path, matching the
            // convention the generated kantoordagApi.ts client already uses for every other call.
            const exchangeUrl = new URL(config.apiEndpoint + '/api/session/firebase-token', window.location.href);

            console.log("Exchanging Microsoft access token for a Firebase custom token");

            const response = await fetch(exchangeUrl, {
                method: 'POST',
                headers: {authorization: `Bearer ${payload.accessToken}`},
            });

            if (!response.ok) {
                throw new Error(`Failed to exchange Microsoft token for a Firebase token (HTTP ${response.status})`);
            }

            const {customToken} = await response.json() as FirebaseTokenExchangeResponse;

            const userCredential = await signInWithCustomToken(payload.auth, customToken);
            const idTokenResult = await userCredential.user.getIdTokenResult();

            console.log("User received; performing login and storing in redux state");

            const email = payload.account.username;

            return {
                email: email,
                displayName: payload.account.name ?? null,
                photoURL: `${config.apiEndpoint}/api/user/${encodeURIComponent(email)}/photo`,
                roles: idTokenResult.claims.groups as Array<string> ?? [],
            }
        }, {
            fulfilled: (state, action) => {
                state.status = LoginStatus.Authenticated
                state.user = action.payload
            }
        });

        const logoutThunk = create.asyncThunk(async () => {
            // No backend call needed any more — sign-in is bearer-token-based, not a server-side
            // session cookie, so there's nothing server-side to invalidate (the old
            // /session/login /session/logout endpoints are removed with this feature).
            console.log("User was null, logging him out");
        }, {
            fulfilled: (state) => {
                state.user = null;
                state.status = LoginStatus.Anonymous
            }
        });

        return ({
            login: loginThunk,
            logout: logoutThunk,
        })
    }
})

export const { login, logout } = userSlice.actions
